<script setup>
import { Deferred } from '@inertiajs/vue3'
import { storeToRefs } from 'pinia'
import RatingMultiSelect from '@/components/RatingMultiSelect.vue'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import DetailCard from './DetailCard.vue'
import DetailCardSkeleton from './DetailCardSkeleton.vue'
import DetailAuthorComponent from './data/DetailAuthorComponent.vue'
import DetailBuildingsComponent from './data/DetailBuildingsComponent.vue'
import DetailComparableTransactionsComponent from './data/DetailComparableTransactionsComponent.vue'
import DetailDocumentComponent from './data/DetailDocumentComponent.vue'
import DetailEhrCommunalComponent from './data/DetailEhrCommunalComponent.vue'
import DetailEhrConstructionComponent from './data/DetailEhrConstructionComponent.vue'
import DetailEhrGeneralComponent from './data/DetailEhrGeneralComponent.vue'
import DetailEhrTechnicalComponent from './data/DetailEhrTechnicalComponent.vue'
import DetailInfraComponent from './data/DetailInfraComponent.vue'
import DetailInteriorComponent from './data/DetailInteriorComponent.vue'
import DetailLandBoardComponent from './data/DetailLandBoardComponent.vue'
import DetailMainComponent from './data/DetailMainComponent.vue'
import DetailMacroMapCardComponent from './data/DetailMapMacroCardComponent.vue'
import DetailMicroMapCardComponent from './data/DetailMapMicroCardComponent.vue'
import DetailPhotoComponent from './data/DetailPhotoComponent.vue'
import DetailPlanningsComponent from './data/DetailPlanningsComponent.vue'
import DetailRealEstateComponent from './data/DetailRealEstateComponent.vue'
import DetailReportQcComponent from './data/DetailReportQcComponent.vue'
import DetailRestrictionsComponent from './data/DetailRestrictionsComponent.vue'
import EhrBuildingPartComponent from './data/EhrBuildingPartComponent.vue'
import { usePage } from '@inertiajs/vue3'
import { computed, useTemplateRef } from 'vue'
import { useFeature } from '@/composables/useFeature'

const store = useDetailStore()
const evaluationStore = useEvaluationStore()
const page = usePage()

// RED-346: the finished-report upload step. The server already withholds the
// `reportQc` prop without the flag + `report-qc` permission; the client checks
// mirror that so the card disappears instantly on a flag flip.
const reportQcFeatureEnabled = useFeature('report-qc')
const reportQcVisible = computed(
  () =>
    reportQcFeatureEnabled.value &&
    !!page.props.auth?.can?.['report-qc'] &&
    page.props.reportQc != null,
)

const props = defineProps({
  objectId: String,
  ehrCode: String,
  evaluationApprover: {
    type: Array,
    default: () => [],
  },
  // Land parcel (maatükk, RED-265): hide the EHR-building sections; the
  // cadaster-driven ones (landboard, restrictions, plannings, …) stay.
  isLandParcel: {
    type: Boolean,
    default: false,
  },
})

const { ehrConstruction } = storeToRefs(store)

// Maatükk: the parcel's ehitised (loaded by DetailBuildingsComponent) drive
// the EHR documents fetch — there is no main-building ehrCode. Read through
// the store, NOT a destructured `buildings` ref: the template's legacy
// ref="buildings" would bind the component instance into a same-named
// setup-scope variable and corrupt the store state.
const parcelEhrCodes = computed(() =>
  (store.buildings || []).map(b => b.ehrCode).filter(Boolean),
)

const realEstateNoChanged = realEstateNo => {
  const mainForm = { ...store.mainForm }
  mainForm.registriosa_nr = realEstateNo
  store.updateMainForm(mainForm)
}

const realEstateOwnerChanged = owner => {
  const mainForm = { ...store.mainForm }
  mainForm.omanik = owner
  store.updateMainForm(mainForm)
}

const detailContainer = useTemplateRef('detailContainer')
</script>

<template>
  <div ref="detailContainer">
    <div id="section-main">
      <DetailAuthorComponent :evaluation-approver />
      <DetailMainComponent />
    </div>
    <hr class="my-4 border-gray-200 dark:border-gray-700" />
    <div id="section-maps" class="flex w-full gap-4 mb-4">
      <div class="flex-1">
        <Deferred data="propertyData">
          <template #fallback>
            <DetailCardSkeleton>
              <template #heading>
                <span class="pi pi-map-marker"></span>
                {{ $t(`messages.detail.map.macro`) }}
              </template>
            </DetailCardSkeleton>
          </template>
          <DetailMacroMapCardComponent />
        </Deferred>
      </div>
      <div class="flex-1">
        <Deferred data="propertyData">
          <template #fallback>
            <DetailCardSkeleton>
              <template #heading>
                <span class="pi pi-map-marker"></span>
                {{ $t(`messages.detail.map.micro`) }}
              </template>
            </DetailCardSkeleton>
          </template>
          <DetailMicroMapCardComponent />
        </Deferred>
      </div>
    </div>
    <div class="flex w-full gap-4 mb-4">
      <div id="section-infra" class="flex-1">
        <Deferred data="propertyData">
          <template #fallback>
            <DetailCardSkeleton>
              <template #heading>
                <span class="pi pi-map-marker"></span>
                {{ $t(`messages.detail.infra.title`) }}
              </template>
              <Skeleton width="100%" height="600px"></Skeleton>

              <div class="p-[20px]">
                <div v-for="i in 6" :key="i" class="grid grid-cols-2 gap-4 mb-2">
                  <Skeleton height="42px"></Skeleton>
                  <Skeleton height="42px"></Skeleton>
                </div>
              </div>
            </DetailCardSkeleton>
          </template>
          <DetailInfraComponent />
        </Deferred>
      </div>
      <div id="section-landboard" class="flex-1">
        <Deferred data="propertyData">
          <template #fallback>
            <DetailCardSkeleton>
              <template #heading>
                <span class="pi pi-map"></span>
                {{ $t(`messages.detail.landboard.title`) }}
              </template>
              <Skeleton border-radius="0" width="100%" height="600px"></Skeleton>

              <div class="p-[20px]">
                <div v-for="i in 26" :key="i" class="grid grid-cols-2 gap-4 mb-2">
                  <Skeleton :width="`${i % 2 === 0 ? '50%' : '100%'}`" height="42px"></Skeleton>
                  <Skeleton :width="`${i % 2 === 0 ? '50%' : '100%'}`" height="42px"></Skeleton>
                </div>
              </div>
            </DetailCardSkeleton>
          </template>
          <DetailLandBoardComponent ref="landBoard" />
        </Deferred>
      </div>
    </div>
    <div v-if="!isLandParcel" class="flex w-full gap-4 mb-4">
      <div id="section-ehr-general" class="flex-1">
        <DetailEhrGeneralComponent />
      </div>
      <div id="section-ehr-technical" class="flex-1">
        <DetailEhrTechnicalComponent />
      </div>
    </div>
    <div class="flex w-full gap-4 mb-4">
      <div v-if="!isLandParcel" id="section-ehr-construction" class="flex-1">
        <DetailEhrConstructionComponent />
      </div>
      <div id="section-ehr-communal" class="flex-1">
        <DetailEhrCommunalComponent :is-land-parcel="isLandParcel" />
      </div>
      <div v-if="isLandParcel" class="flex-1"></div>
    </div>
    <div id="section-real-estate">
      <DetailRealEstateComponent
        ref="realEstate"
        @real-estate-no="realEstateNoChanged"
        @real-estate-owner="realEstateOwnerChanged"
      />
    </div>
    <div v-if="!isLandParcel" id="section-building-parts">
      <EhrBuildingPartComponent :livingroom="true" />
      <EhrBuildingPartComponent :livingroom="false" />
    </div>

    <div v-if="!isLandParcel" class="flex w-full gap-4 mb-4">
      <div class="flex-1">
        <div id="section-panipaik">
          <DetailCard class="mb-4">
            <template #heading>
              <span class="pi pi-clipboard-check"></span>
              {{ $t(`messages.detail.ehrConstruction.panipaik`) }}
            </template>
            <div class="p-4">
              <div class="grid grid-cols-2 gap-4 mb-4">
                <div class="flex flex-col">
                  <label class="font-bold">
                    {{ $t('messages.detail.ehrConstruction.panipaik') }}
                  </label>
                </div>
                <RatingMultiSelect
                  v-model="ehrConstruction.panipaik"
                  field="panipaik"
                  :options="[0, 1, 2]"
                  :maxSelectedLabels="2"
                  :selectionLimit="2"
                  placeholder="Vali kuni 2"
                  size="base"
                  tooltipPosition="right"
                  @update:model-value="evaluationStore.save({ construction: { panipaik: $event } })"
                />
              </div>
            </div>
          </DetailCard>
        </div>

        <div id="section-interior">
          <DetailInteriorComponent />
        </div>
      </div>
      <div id="section-restrictions" class="flex-1">
        <Deferred data="propertyData">
          <template #fallback>
            <DetailCardSkeleton>
              <template #heading>
                <span class="pi pi-exclamation-octagon"></span>
                {{ $t(`messages.detail.restrictions.title`) }}
              </template>
            </DetailCardSkeleton>
          </template>
          <DetailRestrictionsComponent ref="restriction" />
        </Deferred>
      </div>
    </div>
    <div class="flex w-full gap-4 mb-4">
      <div id="section-plannings" class="flex-1">
        <Deferred data="propertyData">
          <template #fallback>
            <DetailCardSkeleton>
              <template #heading>
                <span class="pi pi-exclamation-triangle"></span>
                {{ $t(`messages.detail.plannings.title`) }}
              </template>
            </DetailCardSkeleton>
          </template>
          <DetailPlanningsComponent :object-id="objectId" />
        </Deferred>
      </div>
      <!-- Land mode has no panipaik/interior column, so Kitsendused pairs with Planeeringud here instead. -->
      <div v-if="isLandParcel" id="section-restrictions" class="flex-1">
        <Deferred data="propertyData">
          <template #fallback>
            <DetailCardSkeleton>
              <template #heading>
                <span class="pi pi-exclamation-octagon"></span>
                {{ $t(`messages.detail.restrictions.title`) }}
              </template>
            </DetailCardSkeleton>
          </template>
          <DetailRestrictionsComponent ref="restriction" />
        </Deferred>
      </div>
      <div v-if="!isLandParcel" class="flex-1"></div>
    </div>
    <div class="flex w-full gap-4 mb-4">
      <div v-if="!isLandParcel || parcelEhrCodes.length" id="section-documents" class="flex-1">
        <DetailDocumentComponent
          :ehr-code="isLandParcel ? '' : ehrCode"
          :ehr-codes="isLandParcel ? parcelEhrCodes : []"
          :saved-selected-document-ids="store.mainForm?.selected_documents || []"
        />
      </div>
      <!-- Buildingless maatükk: keep photos half-width and right-aligned, as if Dokumendid were present. -->
      <div v-else class="flex-1"></div>
      <div id="section-photos" class="flex-1">
        <DetailPhotoComponent :object-id="objectId" />
      </div>
    </div>
    <div id="section-buildings" class="flex w-full gap-4 mb-4">
      <div class="flex-1">
        <DetailBuildingsComponent
          ref="buildings"
          :saved-selected-buildings="store.mainForm?.selected_buildings || []"
          :is-land-parcel="isLandParcel"
        />
      </div>
    </div>
    <div id="section-transactions" class="flex w-full gap-4 mb-4">
      <div class="flex-1">
        <DetailComparableTransactionsComponent :object-id="objectId" ref="transactions" />
      </div>
    </div>
    <div v-if="reportQcVisible" id="section-report-qc" class="flex w-full gap-4 mb-4">
      <div class="flex-1">
        <DetailReportQcComponent :object-id="objectId" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .right-switch {
    position: absolute;
    right: 0;
  }
  .detail-map {
    position: relative;
    height: 600px;
    width: 100%;
    margin: 0;

    .map-cover {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      backdrop-filter: blur(5px) grayscale(80%);
      z-index: 3;
    }
  }
</style>
